import React, { useEffect, useState } from "react";
import { Typography, Empty, Spin, Button, message, Tag } from "antd";
import { IconShare, IconDiscount2, IconCopy } from '@tabler/icons-react';
import { useNavigate } from "react-router-dom";
import UserServ from "../service/UserServ";
import CouponServ from "../service/CouponServ";
import OfferServ from "../service/OfferServ";
import SafeImage from "../component/SafeImage";
import GlobalHeader from "../util/GlobalHeader";
import DataTable from "../component/ui/DataTable";

const { Title, Text, Paragraph } = Typography;

const BuyerOffers = () => {
    const navigate = useNavigate();
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchOffers = async (showLoading = true) => {
        try {
            if (showLoading && offers.length === 0) setLoading(true);
            const cached = localStorage.getItem('cached_buyer_offers');
            if (cached && showLoading) {
                try {
                    setOffers(JSON.parse(cached));
                    setLoading(false); // Stop skeleton immediately
                } catch(e) {}
            }

            const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
            let userId = storedUser._id || storedUser.id;

            // Fetch parallel
            const [userRes, couponsRes] = await Promise.all([
                userId ? Promise.resolve({ data: { _id: userId } }) : UserServ.getProfile(),
                CouponServ.getMyCoupons()
            ]);

            if (!userId) {
                userId = userRes?.data?._id || userRes?.data?.id;
            }

            let offersRes = { data: [] };
            if (userId) {
                offersRes = await OfferServ.getByUser(userId);
            }

            let combined = [];
            if (offersRes.data) {
                combined = offersRes.data.map(o => ({
                    ...o,
                    isPercentage: o.discountType ? o.discountType === 'percentage' : (!o.discount || !o.discount.includes('₹')),
                    discount: o.discountValue || (o.discount ? o.discount.replace(/[^0-9]/g, '') : 0)
                }));
            }

            if (couponsRes.data) {
                const assigned = couponsRes.data.map(c => ({
                    title: `Exclusive Reward: ${c.code}`,
                    description: `Special discount just for you! Valid until ${new Date(c.endDate).toLocaleDateString()}. Min order ₹${c.minOrderAmount}.`,
                    code: c.code,
                    discount: c.discountValue,
                    isPercentage: c.discountType === 'percentage',
                    isTargeted: true
                }));
                combined = [...combined, ...assigned];
            }
            
            localStorage.setItem('cached_buyer_offers', JSON.stringify(combined));
            setOffers(combined);
        } catch (error) {
            console.error("Failed to load offers", error);
            message.error("Failed to load offers");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOffers(true);
    }, []);

    const handleShare = (offer) => {
        const shareData = {
            title: 'Nearzon Offer',
            text: `🔥 Hey! Check this out 👇\n\nI've been using *Nearzon* for awesome deals 🛍️\nYou should try it too!\n\n🎁 Use my coupon code: *${offer.code}*\nto get ${offer.isPercentage ? `${offer.discount}%` : `₹${offer.discount}`} OFF on ${offer.title}!\n\n📲 Download now: https://nearzon.com`
        };

        if (navigator.share) {
            navigator.share(shareData)
                .then(() => message.success("Offer shared!"))
                .catch((error) => console.log('Error sharing', error));
        } else {
            handleCopyCode(offer.code);
        }
    };

    const handleCopyCode = (code) => {
        navigator.clipboard.writeText(code);
        message.success("Coupon code copied!");
    }

    const columns = [
        {
            title: 'OFFER DETAILS',
            dataIndex: 'title',
            key: 'title',
            width: '40%',
            render: (text, record) => (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div style={{ width: 48, height: 48, borderRadius: 8, overflow: 'hidden', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {record.imageUrl ? (
                            <SafeImage src={record.imageUrl} alt={record.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <IconDiscount2 size={24} color="#94a3b8" stroke={1.5} />
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Text strong style={{ fontSize: 13, color: '#1e293b' }}>{text}</Text>
                        <Text type="secondary" style={{ fontSize: 11, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>{record.description}</Text>
                        {record.isTargeted && <span style={{ fontSize: 9, color: '#8b5cf6', fontWeight: 700, marginTop: 2 }}>EXCLUSIVE</span>}
                    </div>
                </div>
            )
        },
        {
            title: 'DISCOUNT',
            key: 'discount',
            width: '15%',
            render: (text, record) => (
                <span style={{ fontSize: 13, fontWeight: 700, color: '#f59e0b', background: '#fef3c7', padding: '2px 8px', borderRadius: 4 }}>
                    {record.isPercentage ? `${record.discount}% OFF` : `₹${record.discount} OFF`}
                </span>
            )
        },
        {
            title: 'CODE',
            dataIndex: 'code',
            key: 'code',
            width: '15%',
            render: (code) => (
                <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', background: '#f1f5f9', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => handleCopyCode(code)}>
                    {code}
                    <IconCopy size={12} color="#94a3b8" />
                </span>
            )
        },
        {
            title: 'STATUS',
            key: 'status',
            width: '15%',
            render: (_, record) => {
                const expiryDate = record.endDate ? new Date(record.endDate) : null;
                const isExpired = expiryDate ? expiryDate < new Date() : false;
                const statusText = isExpired ? 'Expired' : record.isActive !== false ? 'Active' : 'Inactive';
                
                const style = isExpired 
                    ? { background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5" }
                    : { background: "#dcfce7", color: "#15803d", border: "1px solid #86efac" };

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "2px 8px",
                            borderRadius: "999px",
                            fontSize: "11px",
                            fontWeight: 600,
                            ...style
                        }}>
                            <span style={{ width: 4, height: 4, borderRadius: "50%", background: style.color, flexShrink: 0 }} />
                            {statusText}
                        </span>
                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>
                            {expiryDate ? expiryDate.toLocaleDateString('en-GB') : 'No Expiry'}
                        </div>
                    </div>
                );
            }
        },
        {
            title: 'ACTION',
            key: 'action',
            width: '15%',
            align: 'right',
            render: (_, record) => (
                <button 
                    onClick={() => handleShare(record)}
                    style={{ background: 'transparent', border: 'none', color: '#3b82f6', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}
                >
                    <IconShare size={14} />
                    Share
                </button>
            )
        }
    ];

    const isEmpty = offers.length === 0;

    return (
        <div className="pcu-padding-x-20">
            <GlobalHeader title={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 800 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #fdf4ff, #f3e8ff)', border: '1px solid rgba(139,92,246,0.25)' }}>
                        <IconDiscount2 size={20} color="#7c3aed" stroke={1.8} />
                    </span>
                    My Offers
                </span>
            }>
                <div className="pcu-margin-top-20" style={{ background: '#fff', borderRadius: 16, border: '1px solid #f1f5f9', padding: '24px' }}>
                    {loading ? <div className="pcu-text-center pcu-padding-50"><Spin /></div> : (
                        isEmpty ? (
                            <div className="pcu-bg-white pcu-radius-16 pcu-padding-40 pcu-text-center">
                                <Empty
                                    image="https://cdn-icons-png.flaticon.com/512/9018/9018889.png"
                                    imageStyle={{ height: 80, opacity: 0.8 }}
                                    description={
                                        <div>
                                            <Title level={5} style={{ fontWeight: 700, color: '#334155' }}>No offers right now.</Title>
                                            <Text style={{ color: '#94a3b8', fontSize: 13 }}>Keep shopping to unlock exclusive deals.</Text>
                                        </div>
                                    }
                                />
                            </div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={offers}
                                rowKey={(record, idx) => record._id || record.id || idx}
                                pagination={{ pageSize: 10 }}
                            />
                        )
                    )}
                </div>
            </GlobalHeader>
        </div>
    );
};

export default BuyerOffers;
